import * as Sentry from '@sentry/react';
import { captureException, SeverityLevel } from '@sentry/react';
import { ErrorLogger } from '../logger/logger.interface';

export class SentryLogger implements ErrorLogger {
  private initialized = false;

  init(config: { dsn: string; environment: string }): void {
    if (this.initialized) return;

    Sentry.init({
      dsn: config.dsn,
      beforeBreadcrumb(breadcrumb, hint) {
        if (breadcrumb.category === 'ui.click') {
          let message = breadcrumb.message;
          const text = hint?.['event']?.target?.innerText;

          if (text) {
            message = text;
          }

          return { ...breadcrumb, message };
        }

        return breadcrumb;
      },

      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      environment: config.environment,
      sendDefaultPii: true,
      integrations: [
        Sentry.replayIntegration({
          mask: ['.sentry-mask', '[data-sentry-mask]'],
          unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
        }),
        Sentry.browserTracingIntegration(),
      ],
      // Session Replay
      replaysSessionSampleRate: 1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      //   replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 1.0,
      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        'https://broker.maia.insure',
        'https://test.broker.maia.insure',
        'https://acceptance.broker.maia.insure',
        'https://api.broker.maia.insure',
        'https://test.api.broker.maia.insure',
        'https://acceptance.api.broker.maia.insure',
      ],
    });

    this.initialized = true;
  }

  captureMessage(message: string, level: SeverityLevel = 'info') {
    Sentry.captureMessage(message, level);
  }

  captureException(
    error: unknown,
    context: Parameters<typeof captureException>[1] extends infer L ? L : never,
  ) {
    Sentry.captureException(error, context);
  }

  setUser(user: { guid: string }) {
    Sentry.setUser({
      id: user.guid,
    });
  }

  setTag(key: string, value: string) {
    Sentry.setTag(key, value);
  }

  setContext(name: string, context: Record<string, unknown>) {
    Sentry.setContext(name, context);
  }
}
